import React, { createContext, useContext } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import cn from '@/utils/cn';
import { tv } from 'tailwind-variants';

export type ITheme = 'white' | null;

interface TooltipThemContextProps {
  showArrow?: boolean;
  theme?: ITheme;
}

const TooltipThemeContext = createContext<TooltipThemContextProps>({
  showArrow: false,
  theme: null,
});

// AIDEV-NOTE: When using asChild with custom components, the component must:
// 1. Use React.forwardRef if the component needs to handle refs
// 2. Spread all props (...props) onto the underlying DOM element
// This allows Radix to pass event handlers and other required props

interface TooltipContentProps extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  disabled?: boolean;
  theme?: ITheme;
  showArrow?: boolean;
}

const tooltipContentVariants = tv({
  base: [
    'z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground tw-animate-in',
    'tw-fade-in-0 tw-zoom-in-95 data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0',
    'data-[state=closed]:tw-zoom-out-95 data-[side=bottom]:tw-slide-in-from-top-2',
    'data-[side=left]:tw-slide-in-from-right-2 data-[side=right]:tw-slide-in-from-left-2',
    'data-[side=top]:tw-slide-in-from-bottom-2',
  ],
  variants: {
    theme: {
      null: '',
      white: [
        'tw-text-very-dark-grey tw-bg-white tw-rounded tw-px-[18.6px] tw-py-[9.8px] tw-max-w-[350px] tw-text-center',
        'tw-leading-[1.4] tw-antialiased tw-will-change-transform',
        'tw-shadow-[0_4px_20px_4px_rgba(0,20,60,0.1),_0_4px_80px_-8px_rgba(0,20,60,0.2)]',
      ],
    },
  },
});

const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow> & { theme?: 'white' | null }
>(({ className, theme, ...props }, ref) => {
  const effectiveTheme = theme ?? useContext(TooltipThemeContext).theme;

  return (
    <TooltipPrimitive.Arrow
      ref={ref}
      className={cn(
        effectiveTheme === 'white' ? 'tw-fill-white' : '',
        className,
      )}
      {...props}
    />
  );
});

TooltipArrow.displayName = 'Tooltip.Arrow';

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, disabled, showArrow = false, sideOffset = 4, theme = null, ...props }, ref) => {
  if (disabled) return null;

  return (
    <TooltipThemeContext.Provider value={{ showArrow, theme }}>
      <TooltipPrimitive.Content
        ref={ref}
        className={cn(
          tooltipContentVariants({ theme }),
          className,
        )}
        sideOffset={sideOffset}
        {...props}
      >
        {showArrow && <TooltipArrow theme={theme} />}
        {props.children}
      </TooltipPrimitive.Content>
    </TooltipThemeContext.Provider>
  );
});

TooltipContent.displayName = 'Tooltip.Content';

const TooltipRoot = TooltipPrimitive.Root;
TooltipRoot.displayName = 'Tooltip';

const TooltipTrigger = TooltipPrimitive.Trigger;
TooltipTrigger.displayName = 'Tooltip.Trigger';

const TooltipPortal = TooltipPrimitive.Portal;
TooltipPortal.displayName = 'Tooltip.Portal';

const TooltipProvider = TooltipPrimitive.Provider;
TooltipProvider.displayName = 'Tooltip.Provider';

const Tooltip = Object.assign(TooltipRoot, {
  Arrow: TooltipArrow,
  Content: TooltipContent,
  Portal: TooltipPortal,
  Provider: TooltipProvider,
  Trigger: TooltipTrigger,
});

export default Tooltip;
